import { Component } from '@theme/component';

/**
 * Compact upward announcement carousel. All messages remain in one grid track,
 * so the longest translation determines height without jumps or clipping.
 * @typedef {object} Refs
 * @property {HTMLElement[]} [slides]
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;
  #interval;
  #controller;
  #motion;
  #transitionUntil = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.#motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const options = { signal: this.#controller.signal };
    this.addEventListener('mouseenter', () => this.suspend(), options);
    this.addEventListener('mouseleave', () => this.resume(), options);
    this.addEventListener('focusin', () => this.suspend(), options);
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.resume();
    }, options);
    document.addEventListener('visibilitychange', () => this.resume(), options);
    this.#motion.addEventListener('change', () => this.resume(), options);
    const slides = this.refs.slides ?? [];
    const active = slides.findIndex((slide) => slide.getAttribute('aria-hidden') === 'false');
    this.#current = Math.max(0, active);
    slides.forEach((slide, index) => { slide.inert = index !== this.#current; });
    this.resume();
  }

  disconnectedCallback() {
    this.suspend();
    this.#controller?.abort();
    super.disconnectedCallback();
  }

  next() { this.current += 1; }
  previous() { this.current -= 1; }

  play(interval = this.autoplayInterval) {
    this.paused = false;
    this.#start(interval);
  }

  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() { return this.hasAttribute('paused'); }
  set paused(paused) { this.toggleAttribute('paused', paused); }

  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  resume() { this.#start(this.autoplayInterval); }

  #start(interval) {
    this.suspend();
    const blocked = this.paused || document.hidden || this.#motion?.matches ||
      this.matches(':hover') || this.contains(document.activeElement);
    this.setAttribute('aria-live', blocked ? 'polite' : 'off');
    if (!this.isConnected || !interval || blocked || (this.refs.slides?.length ?? 0) < 2) return;
    this.#interval = setInterval(() => this.next(), Math.max(5000, interval));
  }

  get autoplay() { return Boolean(this.autoplayInterval); }
  get autoplayInterval() {
    const seconds = Number.parseInt(this.getAttribute('autoplay') ?? '', 10);
    return Number.isFinite(seconds) && seconds > 0 ? Math.max(5, seconds) * 1000 : undefined;
  }

  get current() { return this.#current; }
  set current(value) {
    const slides = this.refs.slides ?? [];
    if (slides.length < 2 || performance.now() < this.#transitionUntil) return;
    const index = ((value % slides.length) + slides.length) % slides.length;
    if (index === this.#current) return;
    this.setAttribute('data-rotated', '');
    const previous = this.#current;
    this.#current = index;
    this.#transitionUntil = performance.now() + (this.#motion?.matches ? 0 : 600);
    slides.forEach((slide, position) => {
      slide.toggleAttribute('data-leaving', position === previous);
      slide.setAttribute('aria-hidden', String(position !== index));
      slide.inert = position !== index;
    });
    this.resume();
  }
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
